import { useRef, useState } from "react";
import { FileText, Key, IdCard, Radio, CircleCheck, CircleAlert, FileSearch, Upload, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Belegcheck-Badge-Leiste + Quick-Upload für ein verkauftes / in-Transport
 * Fahrzeug. Zeigt alle Pflicht- und Zusatzdokumente, klick auf eine fehlende
 * Pille (ZB1 / ZB2 / COC) öffnet einen Datei-Picker; nach erfolgreichem
 * Upload wird der Belegcheck refreshed.
 */
const Pill = ({ ok, label, icon: Icon, title, onClick, uploading, "data-testid": tid }) => {
    const clickable = Boolean(onClick) && !uploading;
    return (
        <button
            type="button"
            disabled={!clickable && !uploading}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (clickable) onClick(e);
            }}
            title={title || (clickable ? `${label} hochladen` : label)}
            data-testid={tid}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                ok
                    ? "bg-green-50 text-green-800 border-green-300 cursor-default"
                    : clickable
                        ? "bg-gray-50 text-gray-500 border-gray-200 hover:bg-[#FFCC00]/20 hover:text-[#111827] hover:border-[#FFCC00] cursor-pointer"
                        : "bg-gray-50 text-gray-400 border-gray-200 cursor-default"
            }`}
        >
            {Icon && <Icon className="w-3 h-3" />}
            <span className={ok ? "" : "line-through"}>{label}</span>
            {uploading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : ok
                    ? <CircleCheck className="w-3 h-3" />
                    : <Upload className="w-3 h-3 text-amber-500" />}
        </button>
    );
};

export default function DocumentChecklist({ documents, listingId, onChanged, compact = false }) {
    const [uploadingKind, setUploadingKind] = useState(null);
    const fileInputRef = useRef(null);
    const pendingKindRef = useRef(null);

    if (!documents) return null;
    const d = documents;
    const missingMain = [!d.has_zb1 && "ZB1", !d.has_zb2 && "ZB2", !d.has_coc && "COC"].filter(Boolean);
    const completeMain = missingMain.length === 0;

    const openPicker = (kind) => {
        if (!listingId) {
            toast.error("Kein Inserat verknüpft");
            return;
        }
        pendingKindRef.current = kind;
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        const kind = pendingKindRef.current;
        pendingKindRef.current = null;
        if (!file || !kind) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Datei zu groß (max 10 MB)");
            return;
        }
        setUploadingKind(kind);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("kind", kind);
            const r = await api.post(`/listings/${listingId}/dispo/upload-document`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            const cls = r.data.classification || {};
            const detectedKind = r.data.document?.kind || kind;
            const KIND_LABELS = {
                zb1: "ZB1", zb2: "ZB2", coc: "COC",
                sale_contract: "Kaufvertrag", invoice: "Rechnung",
                handover_protocol: "Übergabeprotokoll", insurance: "Versicherung",
                other: "Beleg",
            };
            if (kind === "other" && cls.source === "ai" && detectedKind !== "other") {
                toast.success(
                    `KI erkannt: ${KIND_LABELS[detectedKind] || detectedKind} · ${file.name}`,
                    { description: cls.confidence ? `Konfidenz ${Math.round(cls.confidence * 100)}%` : undefined }
                );
            } else if (kind === "other" && detectedKind === "other") {
                toast.message(`Beleg gespeichert: ${file.name}`, {
                    description: cls.mocked ? undefined : "KI konnte den Typ nicht eindeutig zuordnen.",
                });
            } else {
                toast.success(`${KIND_LABELS[detectedKind] || detectedKind.toUpperCase()} hochgeladen: ${file.name}`);
            }
            if (typeof onChanged === "function") onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setUploadingKind(null);
        }
    };

    return (
        <div className={compact ? "" : "mt-2 pt-2 border-t border-dashed border-gray-200"} data-testid={`doc-checklist-${listingId || "x"}`}>
            <div className="flex items-center justify-between flex-wrap gap-1 mb-1">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 inline-flex items-center gap-1">
                    <FileSearch className="w-3 h-3" /> Belegcheck
                </div>
                {completeMain ? (
                    <span className="text-[10px] font-bold text-green-700 inline-flex items-center gap-1" data-testid="doc-check-complete">
                        <CircleCheck className="w-3 h-3" /> alle Hauptdokumente da
                    </span>
                ) : (
                    <span className="text-[10px] font-bold text-amber-700 inline-flex items-center gap-1" data-testid="doc-check-missing">
                        <CircleAlert className="w-3 h-3" /> fehlt: {missingMain.join(", ")} · klicken zum Hochladen
                    </span>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-1">
                <Pill
                    ok={d.has_zb1}
                    label="ZB1"
                    icon={FileText}
                    title={d.has_zb1 ? "Zulassungsbescheinigung Teil 1" : "ZB1 nachreichen — Datei hochladen"}
                    onClick={d.has_zb1 ? null : () => openPicker("zb1")}
                    uploading={uploadingKind === "zb1"}
                    data-testid="doc-pill-zb1"
                />
                <Pill
                    ok={d.has_zb2}
                    label="ZB2"
                    icon={FileText}
                    title={d.has_zb2 ? "Zulassungsbescheinigung Teil 2 (Fahrzeugbrief)" : "ZB2 nachreichen — Datei hochladen"}
                    onClick={d.has_zb2 ? null : () => openPicker("zb2")}
                    uploading={uploadingKind === "zb2"}
                    data-testid="doc-pill-zb2"
                />
                <Pill
                    ok={d.has_coc}
                    label="COC"
                    icon={FileText}
                    title={d.has_coc ? "Certificate of Conformity" : "COC nachreichen — Datei hochladen"}
                    onClick={d.has_coc ? null : () => openPicker("coc")}
                    uploading={uploadingKind === "coc"}
                    data-testid="doc-pill-coc"
                />
                {d.key_count != null && (
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border bg-gray-50 text-gray-700 border-gray-300"
                        title={`Schlüssel: ${d.key_count}`}
                        data-testid="doc-pill-keys"
                    >
                        <Key className="w-3 h-3" />
                        {d.key_count}×
                    </span>
                )}
                {d.has_handsender && (
                    <Pill ok label="Handsender" icon={Radio} title="Funk-Handsender vorhanden" data-testid="doc-pill-handsender" />
                )}
                {d.has_keyless_transponder && (
                    <Pill ok label="Keyless" icon={Radio} title="Keyless-Transponder vorhanden" data-testid="doc-pill-keyless" />
                )}
                {d.has_immobilizer_card && (
                    <Pill ok label="WF-Karte" icon={IdCard} title="Wegfahrsperren-/Code-Karte" data-testid="doc-pill-wfcard" />
                )}
                {d.extra_count > 0 && (
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border bg-[#FFCC00]/20 text-[#111827] border-[#FFCC00]"
                        title="Sonstige hinterlegte Belege"
                        data-testid="doc-pill-extras"
                    >
                        <FileText className="w-3 h-3" /> +{d.extra_count} Beleg{d.extra_count === 1 ? "" : "e"}
                    </span>
                )}
                {/* Add-other Beleg */}
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPicker("other"); }}
                    disabled={uploadingKind === "other"}
                    title="Beleg hinzufügen — KI erkennt den Typ automatisch (Kaufvertrag, Rechnung, ZB1/ZB2/COC, …)"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-dashed border-gray-300 text-gray-600 hover:border-[#111827] hover:text-[#111827] hover:bg-gray-50 cursor-pointer"
                    data-testid="doc-pill-add-other"
                >
                    {uploadingKind === "other" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Beleg
                </button>
            </div>

            {/* Aufklappbare Liste der zusätzlichen Belege mit direktem Link */}
            {Array.isArray(d.extras) && d.extras.length > 0 && (
                <ul className="mt-1.5 text-[11px] text-gray-700 space-y-0.5" data-testid="doc-extras-list">
                    {d.extras.map((x, i) => (
                        <li key={i} className="inline-flex items-center gap-1 mr-3">
                            <FileText className="w-3 h-3 text-gray-400" />
                            {x.url ? (
                                <a
                                    href={x.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-[#111827] truncate max-w-[200px]"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`doc-extra-link-${i}`}
                                >
                                    {x.name || `Beleg ${i + 1}`}
                                </a>
                            ) : (
                                <span className="truncate max-w-[200px]">{x.name || `Beleg ${i + 1}`}</span>
                            )}
                            {x.kind && (
                                <span className="text-[9px] uppercase tracking-wider font-bold text-gray-500">· {x.kind}</span>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {/* Versteckter File-Input für Quick-Upload */}
            <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/*"
                onChange={handleFile}
                className="hidden"
                data-testid="doc-checklist-file-input"
            />
        </div>
    );
}
